import { useEffect, useState } from "react";
import { TOPIC_LINKS } from "../../data/topics";
import { Link } from "react-router-dom";
import { MapPin, Mail, Clock, Instagram, CalendarDays, Gift } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { SectionHead } from "./Reveal";
import { Logo } from "./Header";
import { CONTACT, IMG, selectTopic, VOUCHER_SEASON, pageCta } from "../../data/content";
import { useLocation } from "react-router-dom";
import { useContent } from "../../content/ContentContext";
import { FirstVisit } from "./HomeExtras";


const TOP_FAQ = ["Kann ich ein Probetraining machen?", "Bin ich zu alt oder zu unsportlich?", "Was benötige ich für das erste Training?", "Was ist der Unterschied zwischen gecoachtem Kurs und Open Gym?", "Sind die Performance-Kurse nur für Fortgeschrittene?", "Kann ich auch ohne Mitgliedschaft mitmachen?"];
const rank = (q) => { const i = TOP_FAQ.indexOf(q); return i < 0 ? 99 : i; };

export const Faq = ({ compact = false }) => {
  const { faq } = useContent();
  const [all, setAll] = useState(false);
  const sorted = [...faq].sort((x, y) => rank(x.q) - rank(y.q));
  const shown = compact ? 3 : 6;
  const list = all ? sorted : sorted.slice(0, shown);
  return (
  <section id="faq" data-testid="section-faq" className="py-14 md:py-20 scroll-mt-24">
    <div className="max-w-[960px] mx-auto px-5 md:px-10">
      <SectionHead id="faq" eyebrow="Gut zu wissen" title="Deine Fragen vor dem ersten Training." />
      <Accordion type="single" collapsible className="grid gap-2">
        {list.map((f, i) => (
          <AccordionItem key={f.q} value={`f${i}`} className="card !border px-6 md:px-8 data-[state=open]:border-[#BA00C5]/40 transition-colors">
            <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-6 text-left font-head text-lg font-semibold hover:no-underline hover:text-[#BA00C5]">{f.q}</AccordionTrigger>
            <AccordionContent data-testid={`faq-content-${i}`} className="text-base muted leading-relaxed pb-6">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      {!compact && !all && sorted.length > shown && <button type="button" data-testid="faq-more-btn" onClick={() => setAll(true)} className="tap mt-6 inline-flex items-center gap-1.5 text-sm font-semibold underline underline-offset-4 decoration-[#BA00C5]/50 hover:text-[#8A0093]">Weitere {sorted.length - shown} Fragen anzeigen</button>}
      {compact && <Link to="/kurse#faq" data-testid="faq-all-link" className="inline-block mt-6 text-sm font-semibold underline underline-offset-4 hover:text-[#8A0093]">Alle Fragen zu unseren Kursen</Link>}
    </div>
  </section>
  );
};

const Item = ({ icon: Icon, children, testid }) => (
  <div data-testid={testid} className="flex gap-4">
    <span className="w-11 h-11 rounded-full bg-[#F7F6F3] border border-[#E6E3DD] flex items-center justify-center shrink-0"><Icon size={18} className="text-[#BA00C5]" /></span>
    <div className="pt-2.5 flex-1">{children}</div>
  </div>
);

export const Contact = ({ visit = true }) => (
  <section id="kontakt" data-testid="section-kontakt" className="py-12 md:py-16 bg-white border-t border-[#E6E3DD] scroll-mt-24">
    <div className="max-w-[1320px] mx-auto px-5 md:px-10 grid md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
      <div>
        <p className="eyebrow mb-5">Kontakt</p>
        <h2 data-testid="contact-heading" className="h-display text-2xl sm:text-3xl lg:text-4xl">Komm vorbei. Wir freuen uns auf dich.</h2>
        <p className="mt-4 text-sm muted leading-relaxed">Am besten erreichst du uns per E-Mail. Wir antworten persönlich, meist innerhalb eines Werktags.</p>
      </div>
      <div className="space-y-6">
        <Item icon={Mail} testid="contact-email"><a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="font-head font-semibold hover:text-[#BA00C5] whitespace-nowrap text-[15px] sm:text-base">{CONTACT.email}</a><span className="block text-xs muted mt-0.5">Bevorzugter Kontaktweg</span></Item>
        <Item icon={MapPin} testid="contact-address">{CONTACT.address}</Item>
      </div>
      <Item icon={Clock} testid="contact-hours">
        <p className="font-head font-bold mb-3">Öffnungszeiten</p>
        {CONTACT.hours.map(([d, h]) => h.length > 40
          ? <p key={d} className="mt-3 text-xs muted leading-relaxed"><span className="font-semibold text-[#161616]">{d}:</span> {h}</p>
          : <p key={d} className="flex flex-wrap justify-between gap-x-4 gap-y-1 text-sm"><span className="whitespace-nowrap">{d}</span><span className="muted">{h}</span></p>)}
      </Item>
    </div>
    {visit && <div className="max-w-[1320px] mx-auto px-5 md:px-10"><FirstVisit compact /></div>}
  </section>
);

export const Footer = () => {
  const { instagram } = useContent();
  return (
  <footer data-testid="site-footer" className="bg-[#161616] text-white pb-24 md:pb-0">
    <div className="max-w-[1320px] mx-auto px-5 md:px-10 py-12 flex flex-col md:flex-row gap-8 md:items-center justify-between">
      <div className="flex flex-col gap-4">
        <Link to="/" data-testid="footer-logo-link"><img src="/logo-white.webp" alt="FlensFitness" loading="lazy" width={160} height={32} className="h-8 w-auto" /></Link>
        <div className="flex flex-col items-start gap-3 opacity-70">
          <img src={IMG.hyroxLogo} alt="HYROX Training Club" width="600" height="33" loading="lazy" decoding="async" className="h-3.5 w-auto max-w-full object-contain object-left" />
          <img src={IMG.athxLogo} alt="ATHX Affiliate Gym" width="900" height="134" loading="lazy" decoding="async" className="h-4 w-auto max-w-full object-contain object-left" />
          <a href={instagram.url} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" className="tap inline-flex items-center gap-1.5 text-sm hover:text-white"><Instagram size={15} />@{instagram.handle}</a>
        </div>
      </div>
      <nav aria-label="Weitere Seiten" className="flex flex-wrap max-w-xl gap-x-6 gap-y-4 text-sm text-white/70">
        {[['Kurse', '/kurse', 'kurse'], ['Kursplan', '/kursplan', 'kursplan'], ['Coaching', '/coaching', 'coaching'], ['Preise', '/preise', 'preise'], ['Firmenfitness', '/firmenfitness', 'firmenfitness'], ['Für Unternehmen', '/unternehmen', 'unternehmen'], ['Über uns', '/ueber-uns', 'ueber-uns']].map(([label, to, key]) => <Link key={to} to={to} data-testid={`footer-${key}-page-link`} className="tap hover:text-white">{label}</Link>)}
        <button onClick={() => selectTopic("firmenfitness")} data-testid="footer-firmenfitness-link" className="tap hover:text-white">Epassi / Wellpass freischalten</button>
        {VOUCHER_SEASON && <Link to="/coaching#gutschein" data-testid="footer-gutschein-link" className="tap inline-flex items-center gap-1.5 text-[#E9B3EE] hover:text-white"><Gift size={14} /> Gutschein verschenken</Link>}
        <Link to="/impressum" data-testid="footer-impressum-link" className="tap hover:text-white">Impressum</Link>
        <Link to="/hausordnung" data-testid="footer-hausordnung-link" className="tap hover:text-white">Hausordnung</Link>
        <Link to="/datenschutz" data-testid="footer-datenschutz-link" className="tap hover:text-white">Datenschutz</Link>
      </nav>
    </div>
      <nav aria-label="Training in Flensburg" data-testid="footer-topics" className="max-w-[1320px] mx-auto px-5 md:px-10 pb-10 flex flex-wrap gap-x-6 gap-y-3 text-xs text-white/65 border-t border-white/10 pt-6">
        <span className="text-white/60">Training in Flensburg:</span>
        {TOPIC_LINKS.map((t) => <Link key={t.slug} to={`/${t.slug}`} data-testid={`footer-topic-${t.slug}`} className="tap hover:text-white">{t.label}</Link>)}
      </nav>
      <p data-testid="footer-copyright" className="max-w-[1320px] mx-auto px-5 md:px-10 pb-8 text-xs sm:text-sm text-white/50 whitespace-nowrap">© {new Date().getFullYear()} FlensFitness · Fitnessstudio Flensburg</p>
  </footer>
  );
};

const useHideNearForms = (pathname) => {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const seen = new Set();
    let typing = false;
    const update = () => setHidden(typing || seen.size > 0);
    const io = new IntersectionObserver((entries) => { entries.forEach((e) => (e.isIntersecting ? seen.add(e.target) : seen.delete(e.target))); update(); }, { threshold: 0.1 });
    const scan = () => document.querySelectorAll("main form, #anfrage, [data-testid=hero-primary-cta]").forEach((el) => io.observe(el));
    const t = setTimeout(scan, 300);
    let raf = 0;
    const mo = new MutationObserver(() => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; scan(); }); });
    mo.observe(document.body, { childList: true, subtree: true });
    const isField = (el) => el?.matches?.("input, textarea, select");
    const onIn = (e) => { if (isField(e.target)) { typing = true; update(); } };
    const onOut = () => { typing = false; update(); };
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => { clearTimeout(t); cancelAnimationFrame(raf); io.disconnect(); mo.disconnect(); document.removeEventListener("focusin", onIn); document.removeEventListener("focusout", onOut); };
  }, [pathname]);
  return hidden;
};

export const MobileCta = () => {
  const { pathname } = useLocation();
  const cta = pageCta(pathname);
  const hidden = useHideNearForms(pathname);
  return (
  <nav aria-label="Schnellzugriff" data-testid="mobile-sticky-bar" data-hidden={hidden || undefined} aria-hidden={hidden || undefined} className={`md:hidden fixed bottom-0 inset-x-0 z-40 p-3 bg-[#F7F6F3]/90 backdrop-blur-xl border-t border-[#E6E3DD] grid grid-cols-[1fr_auto] gap-2 transition-transform duration-200 ${hidden ? "translate-y-full pointer-events-none" : ""}`}>
    <button data-testid="mobile-sticky-cta" onClick={cta.run} className="btn-accent w-full !px-4">{cta.label}</button>
    <Link to="/kursplan" data-testid="mobile-sticky-kursplan" aria-label="Kursplan" className="btn-ghost !px-4 !bg-white"><CalendarDays size={18} /></Link>
  </nav>
  );
};

// Logo is re-exported for pages that import it from here
export { Logo };
